import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { MemberSession } from "../../shared/types";
import { api } from "./api";

interface AuthValue {
  session: MemberSession | null;
  loading: boolean;
  signup(email: string, password: string): Promise<MemberSession>;
  login(email: string, password: string): Promise<MemberSession>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<MemberSession | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.session());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const listener = () => void refresh();
    window.addEventListener("bucks:demo-change", listener);
    return () => window.removeEventListener("bucks:demo-change", listener);
  }, [refresh]);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      loading,
      signup: async (email, password) => {
        const next = await api.signup(email, password);
        setSession(next);
        return next;
      },
      login: async (email, password) => {
        const next = await api.login(email, password);
        setSession(next);
        return next;
      },
      logout: async () => {
        await api.logout();
        setSession(null);
      },
      refresh,
    }),
    [loading, refresh, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
